import { Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TemplateRef } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AssistantOption, BillingService } from './billing.service';
import { DisciplineConfigService } from './discipline-config.service';

export interface TimeDetails {
  startTime: string;
  endTime: string;
}

export interface BMIDetails {
  weight: number;
  height: number;
  BMI: string;
}

export interface AssistantDetails {
  assistantName: string;
  assistantPracticeNumber: string;
}

@Injectable({
  providedIn: 'root'
})
export class CodePromptingService {

  constructor(private dialog: MatDialog, private disciplineConfigService: DisciplineConfigService, private injector: Injector) { }

  shouldShowTimeFields(code: string): boolean {
    const codesRequiringTime = [
      '0011', '0023', '1847', '1454',
      '821020', '821021', '821022', '821023',
      '821050', '821051', '821052', '821053'
    ];

    return codesRequiringTime.includes(code);
  }

  // 0008/0009 only mean "surgical assistant" for disciplines that have assistant
  // rules. Physiotherapy re-uses the same two numbers for unrelated fee
  // modifiers, so the discipline check is what keeps the prompt off those.
  shouldShowAssistantFields(code: string, disciplineCode: string): boolean {
    const codesRequiringAssistant = ['0008', '0009'];

    return codesRequiringAssistant.includes(code) && this.disciplineConfigService.hasAssistantRules(disciplineCode);
  }

  async openStartFinishDialog(timeDialogTemplate: TemplateRef<any>, seed?: Partial<TimeDetails>): Promise<TimeDetails | null> {
    const ref = this.dialog.open(timeDialogTemplate, { autoFocus: false, disableClose: true });

    // The dialog template binds [(ngModel)] to the parent component's
    // `timeAdditionalInfo` and closes via `[mat-dialog-close]="timeAdditionalInfo"`,
    // so the user's input arrives here as the `results` payload.
    // Previously this method returned a local empty default instead of `results`,
    // which silently discarded everything the user typed.
    // `seed` holds the values the dialog was opened with and only fills in
    // fields the payload came back without.
    const results = await ref.afterClosed().toPromise();
    if (results) {
      const startTime = results.startTime || seed?.startTime || '';
      const endTime = results.endTime || seed?.endTime || '';
      if (startTime || endTime) {
        return { startTime, endTime };
      }
    }
    return null;
  }

  async openAssistantDialog(assistantDialogTemplate: TemplateRef<any>, seed?: Partial<AssistantDetails>): Promise<AssistantDetails | null> {
    const ref = this.dialog.open(assistantDialogTemplate, { autoFocus: false, disableClose: true });

    // Same payload convention as openStartFinishDialog — the caller's template
    // two-way binds its own field and closes via `[mat-dialog-close]="thatField"`.
    // Cancelling returns null, but callers must still add the code: the name is
    // informational and never feeds into pricing.
    // `??` rather than `||` so a deliberately cleared name isn't refilled from `seed`.
    const results = await ref.afterClosed().toPromise();
    if (results) {
      return {
        assistantName: results.assistantName ?? seed?.assistantName ?? '',
        assistantPracticeNumber: results.assistantPracticeNumber ?? seed?.assistantPracticeNumber ?? ''
      };
    }
    return null;
  }

  // BillingService loads the whole group's billing in its constructor, so it is
  // resolved on demand instead of injected — the assistant list is only needed
  // once a 0008/0009 is actually being billed.
  async getPreviousAssistants(groupId?: string): Promise<AssistantOption[]> {
    if (!groupId) {
      return [];
    }

    try {
      const results = await firstValueFrom(this.injector.get(BillingService).getAssistantsByGroup(groupId));
      return results?.data || [];
    } catch {
      return [];
    }
  }

  async openBMIDialog(bmiDialogTemplate: TemplateRef<any>, patient?: any): Promise<BMIDetails | null> {
    const ref = this.dialog.open(bmiDialogTemplate, { autoFocus: false, disableClose: true });

    // Same fix as openStartFinishDialog — return the dialog payload rather
    // than the local default so user input isn't discarded.
    const results = await ref.afterClosed().toPromise();
    if (results && (results.weight || results.height)) {
      return {
        weight: Number(results.weight) || 0,
        height: Number(results.height) || 0,
        BMI: results.BMI || ''
      };
    }
    return null;
  }

  calculateBMI(weight: number, height: number): string {
    const heightInMeters = height / 100; // Convert height from cm to meters
    if (weight && height) {
      return (weight / (heightInMeters * heightInMeters)).toFixed(2);
    }
    return '';
  }
} 